import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Code2,
  Copy,
  KeyRound,
  Link2,
  MapPinned,
  Package,
  RefreshCcw,
  ShieldCheck,
  Truck,
  Webhook,
} from "lucide-react";
import { brand } from "./shared/brand";
import { MarketplaceFooter } from "./shared/MarketplaceFooter";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.fastmax.pe";

const sections = [
  { id: "overview", label: "Resumen" },
  { id: "auth", label: "Autenticación" },
  { id: "locations", label: "Ubicaciones" },
  { id: "products", label: "Productos" },
  { id: "deliveries", label: "Entregas" },
  { id: "webhooks", label: "Webhooks" },
  { id: "testing", label: "Pruebas" },
];

const steps = [
  {
    icon: KeyRound,
    title: "El vendedor crea un token de conexión",
    text: "El vendedor de Fast Max entra a Integraciones, selecciona el sistema externo y genera un token de un solo uso.",
  },
  {
    icon: Link2,
    title: "El sistema externo activa la conexión",
    text: "Tu aplicación intercambia el token por un API token y un secreto para firmar webhooks.",
  },
  {
    icon: Package,
    title: "Los productos se vinculan",
    text: "Cada producto o variante del sistema externo queda asociado a un producto y SKU de Fast Max.",
  },
  {
    icon: Truck,
    title: "Las órdenes se convierten en entregas",
    text: "Tu sistema envía órdenes a Fast Max usando IDs externos y productos ya vinculados.",
  },
  {
    icon: Webhook,
    title: "Fast Max envía actualizaciones",
    text: "Cada cambio importante de estado se comunica a la URL de webhook configurada.",
  },
];

const endpointGroups = [
  {
    id: "auth",
    eyebrow: "Autorización",
    title: "Activar la conexión de una tienda",
    description:
      "El token de conexión lo crea el vendedor dentro de Fast Max. Es temporal y debe intercambiarse una sola vez.",
    items: [
      {
        method: "POST",
        path: "/api/partners/v1/connections/activate",
        body: `{
  "connection_token": "fm_shopify_conn_xxxxx",
  "external_store_id": "shopify_store_123",
  "external_store_name": "Tienda Demo Shopify",
  "webhook_url": "https://shopify-app.example.com/webhooks/fastmax"
}`,
        response: `{
  "connection": {
    "id": 12,
    "partner_slug": "shopify",
    "status": "active"
  },
  "api_token": "fm_shopify_api_xxxxx",
  "webhook_secret": "fm_whsec_xxxxx"
}`,
      },
    ],
  },
  {
    id: "locations",
    eyebrow: "Cobertura",
    title: "Consultar distritos válidos de Lima y puntos de recojo en provincia",
    description:
      "Usa estos endpoints para mantener los valores de tu sistema alineados 1 a 1 con las ubicaciones de Fast Max antes de crear entregas.",
    items: [
      {
        method: "GET",
        path: "/api/partners/v1/locations/lima-districts",
        response: `{
  "districts": [
    {
      "id": "rimac",
      "name": "Rimac",
      "aliases": ["rimac", "rimac"],
      "delivery_type": "lima"
    }
  ]
}`,
      },
      {
        method: "GET",
        path: "/api/partners/v1/locations/provinces",
        response: `{
  "provinces": [
    {
      "id": "arequipa",
      "name": "Arequipa",
      "cities": ["Arequipa"]
    }
  ]
}`,
      },
      {
        method: "GET",
        path: "/api/partners/v1/locations/pickup-points?province=Arequipa&city=Arequipa",
        response: `{
  "pickup_points": [
    {
      "id": 8,
      "province": "Arequipa",
      "city": "Arequipa",
      "agency_address": "Agencia Arequipa Centro"
    }
  ]
}`,
      },
      {
        method: "POST",
        path: "/api/partners/v1/locations/resolve",
        body: `{
  "delivery_type": "lima",
  "district": "Jesus Maria"
}`,
        response: `{
  "valid": true,
  "delivery_type": "lima",
  "district": {
    "id": "jesus-maria",
    "name": "Jesus Maria"
  }
}`,
      },
    ],
  },
  {
    id: "products",
    eyebrow: "Catálogo",
    title: "Crear o vincular productos",
    description:
      "Para productos variables, envía una vinculación por cada variante. Luego los ítems de la entrega pueden referenciar el ID de producto y variante del sistema externo.",
    auth: true,
    items: [
      {
        method: "GET",
        path: "/api/partners/v1/products",
        response: `{
  "products": [
    {
      "external_product_id": "shopify_prod_001",
      "external_variant_id": "shopify_var_black_m",
      "fastmax_product_id": 501,
      "fastmax_sku": "POLO-NEGRO-M",
      "product_type": "variable"
    }
  ]
}`,
      },
      {
        method: "POST",
        path: "/api/partners/v1/products",
        body: `{
  "external_product_id": "shopify_prod_001",
  "external_variant_id": "shopify_var_black_m",
  "type": "variable",
  "title": "Polo Negro M",
  "sku": "POLO-NEGRO-M",
  "price": 59.9,
  "variant": {
    "color": "Negro",
    "size": "M"
  }
}`,
        response: `{
  "mapping": {
    "id": 31,
    "external_product_id": "shopify_prod_001",
    "external_variant_id": "shopify_var_black_m",
    "fastmax_product_id": 501,
    "fastmax_sku": "POLO-NEGRO-M"
  }
}`,
      },
    ],
  },
  {
    id: "deliveries",
    eyebrow: "Órdenes",
    title: "Crear entregas",
    description:
      "Usa un ID externo de orden estable. Fast Max trata las solicitudes repetidas con el mismo external_order_id como la misma entrega. El campo charge/amount_to_collect es opcional y representa el monto a cobrar al cliente final; si no se envía, Fast Max guarda 0. También puedes consultar una entrega por su fastmax_delivery_id cuando necesites confirmar la verdad operativa más reciente.",
    auth: true,
    items: [
      {
        method: "POST",
        path: "/api/partners/v1/deliveries",
        body: `{
  "external_order_id": "SHOPIFY-ORDER-001",
  "external_order_number": "1001",
  "delivery_type": "lima",
  "delivery_date": "2026-05-12",
  "recipient": {
    "name": "Cliente Test",
    "phone": "999999999"
  },
  "location": {
    "district": "Rimac",
    "address": "Av. Test 123"
  },
  "items": [
    {
      "external_product_id": "shopify_prod_001",
      "external_variant_id": "shopify_var_black_m",
      "quantity": 1
    }
  ],
  "charge": 59.9
}`,
        response: `{
  "partner_delivery": {
    "id": 44,
    "external_order_id": "SHOPIFY-ORDER-001",
    "delivery_type": "lima",
    "fastmax_delivery_id": 9281,
    "tracking_code": "PKG-20260512-0001",
    "status": "pending"
  }
}`,
      },
      {
        method: "POST",
        path: "/api/partners/v1/deliveries",
        body: `{
  "external_order_id": "SHOPIFY-PROV-001",
  "delivery_type": "provincia",
  "delivery_date": "2026-05-12",
  "recipient": {
    "name": "Cliente Provincia",
    "phone": "999999999",
    "dni": "12345678"
  },
  "location": {
    "province": "Arequipa",
    "city": "Arequipa",
    "agency_pickup_point_id": 8
  },
  "items": [
    {
      "fastmax_sku": "POLO-NEGRO-M",
      "quantity": 1
    }
  ],
  "charge": 59.9
}`,
      },
      {
        method: "GET",
        path: "/api/partners/v1/deliveries/fastmax/9281?delivery_type=lima",
        response: `{
  "delivery": {
    "id": 44,
    "external_order_id": "SHOPIFY-ORDER-001",
    "external_order_number": "1001",
    "delivery_type": "lima",
    "fastmax_delivery_id": 9281,
    "tracking_code": "PKG-20260512-0001",
    "tracking_url": "https://www.fastmaxlima.pe/#/entrega/9281",
    "status": "postergado",
    "delivery": {
      "recipient": {
        "name": "Cliente Test",
        "phone": "999999999",
        "dni": null
      },
      "location": {
        "district": "Rimac",
        "address": "Av. Test 123",
        "route_zone_slug": "lima-norte"
      },
      "schedule": {
        "delivery_date": "2026-05-13T00:00:00",
        "arrival_time": null,
        "postponed_from_date": "2026-05-12T00:00:00",
        "postponed_reason": "Cliente solicito nueva fecha"
      },
      "payment": {
        "charge": 59.9,
        "charged": null,
        "transfer": false,
        "transfer_client": false
      },
      "product_summary": "1x Polo Negro M",
      "driver_assigned": true
    },
    "status_history": [
      {
        "from_status": "pending",
        "to_status": "encamino",
        "reason": null,
        "created_at": "2026-05-12T15:40:00-05:00"
      },
      {
        "from_status": "encamino",
        "to_status": "postergado",
        "reason": "Cliente solicito nueva fecha",
        "from_delivery_date": "2026-05-12T00:00:00",
        "to_delivery_date": "2026-05-13T00:00:00",
        "created_at": "2026-05-12T17:10:00-05:00"
      }
    ],
    "postergado_history": [
      {
        "previous_delivery_date": "2026-05-12T00:00:00",
        "new_delivery_date": "2026-05-13T00:00:00",
        "reason": "Cliente solicito nueva fecha",
        "created_at": "2026-05-12T17:10:00-05:00"
      }
    ],
    "package": {
      "tracking_code": "PKG-20260512-0001",
      "status": "with_driver",
      "history": [
        {
          "previous_status": "in_warehouse",
          "new_status": "with_driver",
          "timestamp": "2026-05-12T09:20:00-05:00"
        }
      ]
    }
  }
}`,
      },
    ],
  },
  {
    id: "webhooks",
    eyebrow: "Callbacks",
    title: "Recibir actualizaciones de estado de Fast Max",
    description:
      "Fast Max envía eventos JSON firmados a la URL de webhook configurada durante la activación.",
    auth: false,
    items: [
      {
        method: "POST",
        path: "https://shopify-app.example.com/webhooks/fastmax",
        body: `Encabezados:
X-FastMax-Event: delivery.status_updated
X-FastMax-Event-Id: 884
X-FastMax-Signature: sha256=...

Cuerpo:
{
  "event": "delivery.status_updated",
  "external_order_id": "SHOPIFY-ORDER-001",
  "fastmax_delivery_id": 9281,
  "delivery_type": "lima",
  "tracking_code": "PKG-20260512-0001",
  "previous_status": "pending",
  "status": "encamino",
  "delivery": {
    "recipient_name": "Cliente Test",
    "district": "Rimac",
    "arrival_time": "2026-05-12T15:40:00"
  }
}`,
      },
      {
        method: "POST",
        path: "/api/partners/v1/webhook-test",
        body: `{
  "external_order_id": "SHOPIFY-ORDER-001"
}`,
        response: `{
  "event": {
    "id": 885,
    "event_type": "webhook.test",
    "status": "sent"
  }
}`,
      },
    ],
  },
];

function scrollToDocSection(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function CodeBlock({ children }) {
  return (
    <pre className="max-h-[440px] overflow-x-auto rounded-lg border border-slate-200 bg-slate-950 p-4 text-xs leading-relaxed text-slate-100 shadow-inner">
      <code>{children}</code>
    </pre>
  );
}

function MethodBadge({ method }) {
  const isGet = method === "GET";
  return (
    <span
      className={`inline-flex min-w-[52px] items-center justify-center rounded px-2 py-1 text-[11px] font-black ${
        isGet ? "bg-emerald-50 text-emerald-700" : "bg-fuchsia-50 text-fuchsia-700"
      }`}
    >
      {method}
    </span>
  );
}

function EndpointCard({ endpoint }) {
  return (
    <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <MethodBadge method={endpoint.method} />
        <code className="break-all rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-800">
          {endpoint.path}
        </code>
      </div>
      {endpoint.body && (
        <div className="mb-4">
          <p className="mb-2 text-xs font-black uppercase text-slate-500">Solicitud</p>
          <CodeBlock>{endpoint.body}</CodeBlock>
        </div>
      )}
      {endpoint.response && (
        <div>
          <p className="mb-2 text-xs font-black uppercase text-slate-500">Respuesta</p>
          <CodeBlock>{endpoint.response}</CodeBlock>
        </div>
      )}
    </article>
  );
}

function EndpointGroup({ group }) {
  return (
    <section id={group.id} className="scroll-mt-24 border-t border-slate-200 py-10">
      <div className="mb-5 flex flex-col gap-2">
        <span className="text-xs font-black uppercase tracking-wide" style={{ color: brand.primary }}>
          {group.eyebrow}
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-black text-slate-950">{group.title}</h2>
          {group.auth && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
              <ShieldCheck className="size-3.5" />
              Requiere Bearer token
            </span>
          )}
        </div>
        <p className="max-w-3xl text-sm leading-relaxed text-slate-600">{group.description}</p>
      </div>
      <div className="grid gap-4">
        {group.items.map((endpoint, index) => (
          <EndpointCard key={`${group.id}-${index}`} endpoint={endpoint} />
        ))}
      </div>
    </section>
  );
}

export default function DeveloperDocs() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <nav className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="FAST MAX" className="h-8 w-auto" />
            <span className="text-sm font-black italic" style={{ color: brand.primary }}>
              FAST MAX
            </span>
          </Link>
          <div className="flex min-w-0 flex-1 justify-end gap-2">
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
            >
              Iniciar sesión
            </Link>
            <button
              type="button"
              onClick={() => scrollToDocSection("auth")}
              className="inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-white"
              style={{ background: brand.primary }}
            >
              Comenzar
              <ArrowRight className="size-3.5" />
            </button>
          </div>
        </nav>
      </header>

      <main>
        <section className="border-b border-slate-200 bg-slate-50">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 lg:grid-cols-[1fr_360px] lg:py-14">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-600">
                <Code2 className="size-3.5" />
                Documentación de API para partners
              </div>
              <h1 className="max-w-4xl text-4xl font-black leading-tight text-slate-950 md:text-5xl">
                Documentación de integración Fast Max
              </h1>
              <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600">
                Usa esta API para conectar cualquier sistema de ventas, ecommerce, ERP o marketplace con Fast Max.
                Tu aplicación puede vincular productos, crear entregas, validar distritos y recibir actualizaciones
                de estado por webhook.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => scrollToDocSection("deliveries")}
                  className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold text-white"
                  style={{ background: brand.primary }}
                >
                  Crear entregas
                  <Truck className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollToDocSection("webhooks")}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                >
                  Payloads de webhook
                  <Webhook className="size-4" />
                </button>
              </div>
            </div>

            <aside className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <p className="mb-2 text-xs font-black uppercase text-slate-500">URL base</p>
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-slate-100 p-3">
                <code className="min-w-0 flex-1 break-all text-xs font-semibold text-slate-800">{API_BASE}</code>
                <Copy className="size-4 shrink-0 text-slate-500" />
              </div>
              <p className="mb-2 text-xs font-black uppercase text-slate-500">Encabezado de autenticación</p>
              <CodeBlock>{`Authorization: Bearer fm_shopify_api_xxxxx`}</CodeBlock>
            </aside>
          </div>
        </section>

        <section id="overview" className="mx-auto max-w-7xl px-4 py-10">
          <div className="grid gap-4 md:grid-cols-5">
            {steps.map(step => {
              const Icon = step.icon;
              return (
                <article key={step.title} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <Icon className="mb-3 size-5" style={{ color: brand.primary }} />
                  <h3 className="mb-2 text-sm font-black text-slate-950">{step.title}</h3>
                  <p className="text-xs leading-relaxed text-slate-600">{step.text}</p>
                </article>
              );
            })}
          </div>
        </section>

        <div className="mx-auto grid max-w-7xl gap-8 px-4 pb-14 lg:grid-cols-[220px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
              <p className="mb-2 px-2 text-xs font-black uppercase text-slate-500">Contenido</p>
              <nav className="grid gap-1">
                {sections.map(section => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => scrollToDocSection(section.id)}
                    className="rounded-md px-2 py-2 text-left text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                  >
                    {section.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          <div>
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-3 text-xl font-black text-slate-950">Reglas principales</h2>
              <div className="grid gap-3 md:grid-cols-2">
                {[
                  "Toda solicitud que cambia datos debe ser idempotente cuando se reutiliza external_order_id.",
                  "Todo producto o variante debe estar vinculado antes de crear entregas.",
                  "Las entregas de Lima requieren un distrito válido de Fast Max.",
                  "Las entregas de provincia requieren una provincia válida y un punto de recojo o dirección de agencia.",
                  "El receptor del webhook debe responder con cualquier estado 2xx después de guardar el evento.",
                  "Las firmas de webhook deben verificarse con el webhook_secret devuelto durante la activación.",
                ].map(rule => (
                  <div key={rule} className="flex gap-2 text-sm leading-relaxed text-slate-600">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                    <span>{rule}</span>
                  </div>
                ))}
              </div>
            </section>

            {endpointGroups.map(group => (
              <EndpointGroup key={group.id} group={group} />
            ))}

            <section id="testing" className="scroll-mt-24 border-t border-slate-200 py-10">
              <div className="mb-5">
                <span className="text-xs font-black uppercase tracking-wide" style={{ color: brand.primary }}>
                  Pruebas locales
                </span>
                <h2 className="mt-2 text-2xl font-black text-slate-950">Flujo de prueba recomendado</h2>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                  Durante el piloto, prueba con ngrok y webhook.site antes de mover el token del partner a producción.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <MapPinned className="mb-3 size-5 text-emerald-600" />
                  <h3 className="mb-2 text-sm font-black text-slate-950">Exponer API local</h3>
                  <CodeBlock>{`ngrok http 5000`}</CodeBlock>
                </article>
                <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <Webhook className="mb-3 size-5" style={{ color: brand.primary }} />
                  <h3 className="mb-2 text-sm font-black text-slate-950">Usar un receptor temporal</h3>
                  <p className="text-sm leading-relaxed text-slate-600">
                    Coloca una URL de webhook.site en la solicitud de activación para inspeccionar payloads firmados.
                  </p>
                </article>
                <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <Clock className="mb-3 size-5 text-amber-600" />
                  <h3 className="mb-2 text-sm font-black text-slate-950">Worker de reintentos</h3>
                  <p className="text-sm leading-relaxed text-slate-600">
                    Los eventos de webhook fallidos se reintentan con el cron job de Render cada dos minutos.
                  </p>
                </article>
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-slate-50 p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-xl font-black text-slate-950">Política de reintentos de webhook</h2>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">
                    Fast Max reintenta webhooks fallidos con backoff y se detiene después de ocho intentos fallidos.
                  </p>
                </div>
                <div className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm">
                  <RefreshCcw className="size-4" />
                  2m, 5m, 15m, 30m, 1h, 2h, 6h, 12h
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <MarketplaceFooter />
    </div>
  );
}
